/**
 * 🏆 배지 컬렉션 컴포넌트
 * 획득한 배지와 미획득 배지를 카테고리별로 표시
 */

import React, { useState } from 'react';
import { BADGES, BADGE_CATEGORIES, BADGE_ICONS, getRarityConfig, getBadgesByCategory } from '../constants/badges';
import { Lock, ChevronRight } from 'lucide-react';

export default function BadgeCollection({ earnedBadges = [], totalPoints = 0, level = 1, levelProgress = 0 }) {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedBadge, setSelectedBadge] = useState(null);

    // 카테고리 목록 (전체에 Trophy 아이콘 추가)
    const categories = [
        { id: 'all', name: '전체', emoji: '🏆', icon: '/images/Trophy.png' },
        ...Object.values(BADGE_CATEGORIES).filter(c => c.id !== 'hidden')
    ];

    // 필터링된 배지 목록
    const filteredBadges = selectedCategory === 'all'
        ? BADGES.filter(b => !b.hidden)
        : getBadgesByCategory(selectedCategory).filter(b => !b.hidden);

    // 배지 획득 여부
    const isEarned = (badgeId) => earnedBadges.includes(badgeId);

    // 획득 배지 수
    const earnedCount = earnedBadges.length;
    const totalCount = BADGES.filter(b => !b.hidden).length;

    return (
        <div className="w-full">
            {/* 헤더 통계 */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-6 mb-6">
                <div className="flex items-center gap-4 mb-4">
                    {/* 레벨 뱃지 */}
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
                        <span className="text-2xl font-black text-white">Lv.{level}</span>
                    </div>
                    <div className="flex-1">
                        <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                            <img src="/images/Trophy.png" alt="" className="w-5 h-5" />
                            사역 여정
                        </h3>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                            {earnedCount}/{totalCount} 배지 획득 •
                            <img src="/images/Star.png" alt="" className="w-4 h-4 inline" />
                            {totalPoints.toLocaleString()}P
                        </p>
                    </div>
                </div>

                {/* 레벨 진행 바 */}
                <div className="bg-white/60 rounded-full h-3 overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-500"
                        style={{ width: `${levelProgress}%` }}
                    />
                </div>
                <p className="text-xs text-gray-500 mt-2 text-right">
                    다음 레벨까지 {Math.round(100 - levelProgress)}%
                </p>
            </div>

            {/* 카테고리 탭 */}
            <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
                {categories.map(cat => (
                    <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`flex items-center gap-1 px-3 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all flex-shrink-0 ${selectedCategory === cat.id
                            ? 'bg-[#354F38] text-white shadow-lg shadow-[#354F38]/20'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                    >
                        {/* 3D 아이콘 */}
                        {cat.icon && (
                            <img src={cat.icon} alt={cat.name} className="w-4 h-4 object-contain flex-shrink-0" />
                        )}
                        <span className="truncate">{cat.name}</span>
                    </button>
                ))}
            </div>

            {/* 배지 그리드 */}
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                {filteredBadges.map(badge => {
                    const earned = isEarned(badge.id);
                    const rarityConfig = getRarityConfig(badge.rarity);
                    const iconPath = BADGE_ICONS[badge.id];

                    return (
                        <button
                            key={badge.id}
                            onClick={() => setSelectedBadge(badge)}
                            className={`relative aspect-square rounded-2xl flex flex-col items-center justify-center p-2 transition-all ${earned
                                ? `bg-gradient-to-br ${rarityConfig.gradient} border-2 ${rarityConfig.borderColor} shadow-lg hover:scale-105`
                                : 'bg-gray-100 border-2 border-gray-200 opacity-50 hover:opacity-70'
                                }`}
                        >
                            {/* 배지 아이콘 */}
                            <div className={`w-12 h-12 flex items-center justify-center ${!earned && 'grayscale'}`}>
                                {earned && iconPath ? (
                                    <img
                                        src={iconPath}
                                        alt={badge.name}
                                        className="w-full h-full object-contain"
                                    />
                                ) : earned ? (
                                    <span className="text-3xl">{badge.emoji}</span>
                                ) : (
                                    <Lock size={24} className="text-gray-400" />
                                )}
                            </div>

                            {/* 희귀도 뱃지 (레어 이상) */}
                            {earned && ['rare', 'epic', 'legendary'].includes(badge.rarity) && (
                                <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full ${badge.rarity === 'legendary' ? 'bg-amber-400' :
                                    badge.rarity === 'epic' ? 'bg-purple-400' : 'bg-blue-400'
                                    } flex items-center justify-center`}>
                                    <span className="text-[10px]">✦</span>
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* 배지 상세 모달 */}
            {selectedBadge && (
                <div
                    className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm"
                    onClick={() => setSelectedBadge(null)}
                >
                    <div
                        className="bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden animate-scale-up"
                        onClick={e => e.stopPropagation()}
                    >
                        {(() => {
                            const earned = isEarned(selectedBadge.id);
                            const rarityConfig = getRarityConfig(selectedBadge.rarity);
                            const iconPath = BADGE_ICONS[selectedBadge.id];

                            return (
                                <>
                                    <div className={`bg-gradient-to-br ${rarityConfig.gradient} p-8 text-center`}>
                                        <div className={`w-24 h-24 mx-auto mb-4 rounded-[2rem] bg-white shadow-xl flex items-center justify-center border-4 ${rarityConfig.borderColor} ${!earned && 'grayscale opacity-50'}`}>
                                            {iconPath ? (
                                                <img
                                                    src={iconPath}
                                                    alt={selectedBadge.name}
                                                    className="w-20 h-20 object-contain"
                                                />
                                            ) : (
                                                <span className="text-5xl">{selectedBadge.emoji}</span>
                                            )}
                                        </div>

                                        <h2 className="text-xl font-black text-gray-900 mb-1">{selectedBadge.name}</h2>
                                        <p className="text-gray-600 text-sm mb-3">{selectedBadge.description}</p>

                                        <div className="flex items-center justify-center gap-3">
                                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${rarityConfig.bgColor} ${rarityConfig.textColor}`}>
                                                ✦ {rarityConfig.name}
                                            </span>
                                            <span className="text-amber-600 font-bold text-sm flex items-center gap-1">
                                                <img src="/images/Star.png" alt="" className="w-4 h-4" />
                                                +{selectedBadge.points}P
                                            </span>
                                        </div>

                                        {!earned && (
                                            <div className="mt-4 px-4 py-2 bg-white/60 rounded-xl flex items-center justify-center gap-2">
                                                <img src="/images/Locked.png" alt="" className="w-4 h-4" />
                                                <p className="text-xs text-gray-500">
                                                    아직 획득하지 못한 배지예요
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="p-4">
                                        <button
                                            onClick={() => setSelectedBadge(null)}
                                            className="w-full py-3 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition"
                                        >
                                            닫기
                                        </button>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
}
